import React, { useCallback, useRef, useState, useEffect } from 'react';
import style from './MissionsCardDetailsInfo.scss';
import { Icon } from 'js/components/common/Icon';
import { usePortletTranslations } from '../../../../system/Portlet/hooks';
import { BannerProgressCircle } from '../BannerProgressCircle';
import { MissionsRewardInfoBlock } from '../MissionsRewardInfoBlock';
import { clsx } from '../../../../../utils/classNames.util';
import Games from '../../Details/Games';
import { MissionCountDown } from '../MissionsStartEndCountDown';
import { useAppSelector } from '../../../../../store/hooks';
import { equals, prop } from 'ramda';

export const MissionsCardDetailsInfo = ({ data, showBoostPrizes, leaderboardEndTime, isLast }) => {

    const {
        nameMission,
        status,
        boostCountLimitUsed,
        boostCountLimit,
        descriptionMission,
        description,
        endDateTime,
        startDateTime,
        rule,
        imageMission,
        urlMission
    } = data;

    const t = usePortletTranslations();

    const content = useRef(null);

    const isFinished = useCallback(() => {
        return status === 'COMPLETED' || status === 'DISABLED'
            || boostCountLimitUsed && boostCountLimit === boostCountLimitUsed;
    }, [boostCountLimitUsed, boostCountLimit, status]);

    const [active, setActive] = useState(!isFinished());
    const [height, setHeight] = useState(isFinished() ? '0px' : `${content.current?.scrollHeight}px`);

    const toggleAccordion = useCallback((_active) => {
        if (_active) {
            setActive(true);
            setHeight(`${content.current?.scrollHeight || 0}px`);
        } else {
            setTimeout(() => { setActive(false); }, 1000);
            setHeight('0px');
        }
    }, [content]);

    useEffect(() => {
        setTimeout(() => {
            if (!isFinished()) {
                setHeight(`${content.current?.scrollHeight}px`);
            } else {
                setHeight('0px');
                setActive(false);
            }
        }, 100);
    }, [status, toggleAccordion, boostCountLimit, boostCountLimitUsed, isFinished]);

    const setCardHeightOnResize = useCallback(() => {
        setTimeout(() => {
            if (active) {
                setHeight(`${content.current?.scrollHeight}px`);
            }
        }, 100);
    }, [active]);

    useEffect(() => {
        window.addEventListener('resize', setCardHeightOnResize);
        return () => {
            window.removeEventListener('resize', setCardHeightOnResize);
        };
    }, [setCardHeightOnResize]);

    const updateCardHeight = () => {
        setHeight(`${content.current?.scrollHeight}px`);
    };

    const onHeaderClick = useCallback(() => {
        toggleAccordion(!active);
    }, [toggleAccordion, active]);

    const missionMapping = {
        notStarted: {
            iconType: 'missionsIconHeaderNotStarted',
            progressCircleColor:'#ccc',
            status: 'notStarted',
            cardWrapperStyle: style['wrapper'],
            progressCircleInsideColor: style['notStartedProgressCircleInsideColor']
        },
        incompleted: {
            iconType: 'missionsIconHeaderIncompleted',
            progressCircleColor: '#ccc',
            status: 'incompleted',
            cardWrapperStyle: style['wrapper'],
            progressCircleInsideColor: style['inCompletedProgressCircleInsideColor']
        },
        completed: {
            iconType: 'missionsIconHeaderCompleted',
            progressCircleColor: 'var(--missions-details-process-completed-bar-and-text-color)',
            status: 'completed',
            cardWrapperStyle: style['wrapperCompleted'],
            progressCircleInsideColor: style['completedProgressCircleInsideColor']
        },
        active: {
            progressCircleColor: 'var(--missions-details-process-bar-and-text-color)',
            status: 'active',
            cardWrapperStyle: style['wrapper'],
            progressCircleInsideColor: style['activeProgressCircleInsideColor']
        }
    };

    const detectMissionState = () => {
        if (status === 'IN_DESIGN' || status === 'APPROVED') {
            return missionMapping['notStarted'];
        } else if ((status === 'COMPLETED' || status === 'DISABLED') && boostCountLimit !== boostCountLimitUsed) {
            return missionMapping['incompleted'];
        } else if (boostCountLimitUsed && boostCountLimit === boostCountLimitUsed) {
            return missionMapping['completed'];
        }
        return missionMapping['active'];
    };
    const state = detectMissionState();
    const { iconType } = state;
    const usedLimitRounded = boostCountLimitUsed || 0;
    const percentage = boostCountLimit && (state.status === 'active' || state.status === 'completed') ? Math.floor(100 * usedLimitRounded / boostCountLimit) : 0;

    const getGamesFromRules = () => {
        const leaderboardRules = [rule];
        const games = [];
        if (leaderboardRules.length > 0) {
            leaderboardRules.forEach((gamesRule) => {
                const gamesFormRule = gamesRule && gamesRule.gameRoundRuleInfo && gamesRule.gameRoundRuleInfo.games;
                if (gamesFormRule) {
                    gamesFormRule.forEach((gameFormLoop) => {
                        games.push({
                            gameCode: gameFormLoop,
                            isNotStarted: Boolean(gamesRule.millisecondsTillRuleStart),
                            isFinished: Boolean(!gamesRule.millisecondsTillRuleStart
                                && !gamesRule.millisecondsTillRuleEnd),
                            isActive: Boolean(gamesRule.millisecondsTillRuleEnd > 0
                                && !gamesRule.millisecondsTillRuleStart),
                            timeToStart: gamesRule.millisecondsTillRuleStart
                            && gamesRule.millisecondsTillRuleStart > 0 ? gamesRule.millisecondsTillRuleStart : 0
                        });
                    });
                }
            });
        }
        return games;
    };

    let gamesSupportedByPortal = useAppSelector(prop('games'));
    // gamesSupportedByPortal could came null.
    if (gamesSupportedByPortal == null) {
        gamesSupportedByPortal = [];
    }

    const getAvailableGamesSupportedByPortal = () => {
        const gamesFromRules = getGamesFromRules();
        const availableGames = gamesFromRules.filter(gameInfo => gamesSupportedByPortal[gameInfo.gameCode]);
        const isEqual = equals(availableGames, gamesFromRules);
        return isEqual ? gamesFromRules : availableGames;
    };

    return (
        <div className={style.missionMainWrapper}>
        <div className={state.cardWrapperStyle}>
            <div className={style.headerWrapper} onClick={onHeaderClick}>
                <div className={style.header}>{nameMission}</div>
                <div className={style.rightHeaderWrapper}>
                    {iconType && !active ? <Icon type={iconType} /> : <div className={style.iconMock}/>}
                    <Icon type="missionsIconArrow" className={clsx([
                        style.icon, active && style.expanded
                    ])} />
                </div>
            </div>
            <div ref={content} style={{ maxHeight: `${height}` }} className={style.accordion__content}>
                <div className={style.description}>{descriptionMission}</div>
                <div className={style.progressWrapper}>
                    <BannerProgressCircle style={state.progressCircleInsideColor}
                                          state={state}
                                          withProgress={true}
                                          size={90}
                                          strokeWidth={20}
                                          percentage={percentage} />
                    <div className={style.countDownWrapper}>
                        {state.status === 'incompleted' && <div className={style.incompleteTextStyle}>{t('mission.incompleted')}</div>}
                        {(state.status === 'notStarted' || state.status === 'active') &&
                        <MissionCountDown
                            endTime={endDateTime}
                            startTime={startDateTime}
                            status={status}
                            campaignEndTime={leaderboardEndTime}/>}
                    </div>
                </div>
                <Games games={getAvailableGamesSupportedByPortal()} isInMissionsItemCard={true}
                       updateCardHeight={updateCardHeight} imageMission={imageMission} urlMission={urlMission}
                       missionStatus={state.status}/>
                {showBoostPrizes && description && <MissionsRewardInfoBlock mainText="missionReward" prizeDescriptionText={description} />}
            </div>

        </div>
            {!isLast && <div className={style.hiddenMargin}/>}
        </div>
    );
};

